import React, { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faUpload } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { IAuthState } from "redux/reducers/auth";

import { IUIActions, showError, showSuccess } from "redux/actions/ui";
import { IProduct, IVariant } from "redux/reducers/models/product";
import { StatusCode } from "api/protocols";
import {
  DeleteTicketImage,
  GetTickets,
  PostTicketImage,
  PutTicket,
  RemoveRedemptionProduct,
  AddRedemptionProducts,
  ArchiveTicket,
} from "api/rpc/2024-04/facilityAdmin/ticket/ticket";

import { TicketStubType } from "pages/secure/facility/customer/context/contextTypes";

import Page from "components/page/Page";
import Card from "components/card/Card";
import { ButtonNew as Button, ButtonNew } from "components/buttonNew";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { Select } from "components/select/";
import Checkbox from "components/form/checkbox/Checkbox";
import "pages/secure/facility/product/Ticket.scss";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import moment from "moment";
import TextEditor from "components/textEditor/textEditor";
import { isEqualWith, isNull } from "lodash";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import Sheet from "components/sheet/Sheet";
import ProductSelect from "components/productModal/ProductSelect";
import Portal from "elements/Portal";
import ReactDOM from "react-dom";
import Popup from "components/popup/Popup";
import { GetFacility } from "api/rpc/2024-04/facilityAdmin/facility/facility";
import { IFacility } from "redux/reducers/models/facility";
import Callout from "components/callout/Callout";

interface ITicketState {
  id: number;
  title: string;
  subtitle?: string;
  description: string;
  expiry_type: string;
  expiry_date: string;
  expiry_days: number;
  valid_facilities: Array<number>;
  facility_ids_list: IFacilityInformation[];
  selectedDate: Date;
  product_redemption: any[];
  logo_image: string;
  ticket_stubs: Array<TicketStubType>;
  archived: boolean;
  terms: Array<string>;
  client_id: number;
  share_limit: string;
  transfers_disabled: boolean;
  multi_use: boolean;
  valid_start_date: Date | null;
  valid_end_date: Date | null;
  variants: IVariant[];
}

interface IFacilityInformation {
  id: any;
  value: any;
  label: any;
  isChecked: boolean;
}

interface ISingleProps {
  authStore: IAuthState;
  uiActions: IUIActions;
}

interface IFilterState {
  showArchiveWarning: boolean;
}

const Single: React.FC<ISingleProps> = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { Option } = Select;
  const { ticketId }: any = useParams();
  const imageUploadInputRef = useRef(null);
  const dispatch = useAppDispatch();

  const { authStore } = useAppSelector(store => store);

  const [redemptionProductModalOpen, setRedemptionProductModalOpen] = useState<boolean>(false);
  const [selectedProducts, setSelectedProducts] = useState<Array<IProduct>>([]);

  const [ticketStateBeforeChanges, setTicketStateBeforeChanges] = useState<ITicketState>(undefined);
  const [ticketLoaded, setTicketLoaded] = useState<boolean>(false);

  const options = [
    { label: t("Date"), value: "date" },
    { label: t("Days"), value: "days" },
    { label: t("None"), value: "none" },
  ];

  const [ticketState, setTicketState] = useState<ITicketState>({
    id: null,
    title: "",
    subtitle: "",
    description: "",
    expiry_type: "",
    expiry_date: "",
    expiry_days: null,
    valid_facilities: [],
    facility_ids_list: [],
    selectedDate: new Date(),
    product_redemption: [],
    logo_image: null,
    ticket_stubs: [],
    archived: false,
    terms: null,
    client_id: null,
    share_limit: "",
    transfers_disabled: false,
    multi_use: false,
    valid_start_date: null,
    valid_end_date: null,
    variants: [],
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    showArchiveWarning: false,
  });

  useEffect(() => {
    void loadTicket();
  }, []);

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setTicketState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleDescriptionChange(value: string) {
    setTicketState(prevState => ({ ...prevState, description: value }));
  }

  function changeFacilityList(facility: IFacility) {
    const updatedFacilities = [...ticketState.valid_facilities];
    const indexFound = ticketState?.valid_facilities?.findIndex(facilityId => facilityId === facility?.id);

    if (indexFound !== -1) {
      updatedFacilities.splice(indexFound, 1);
    } else {
      updatedFacilities.push(facility?.id);
    }

    setTicketState(prevState => ({
      ...prevState,
      valid_facilities: updatedFacilities,
    }));
  }

  async function loadTicket() {
    const facilityRes = await GetFacility(
      {
        "client-facilities": true,
      },
      true,
    );

    if (facilityRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.ticket_edit.032")));
      return;
    }

    const tempFacilityIdList: IFacilityInformation[] = [];

    const params = {
      id: ticketId,
      extended: true,
    };

    const loadTicketRes = await GetTickets(params, true);
    if (loadTicketRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.ticket_edit.033")));
      return;
    }

    const ticket = loadTicketRes.data[0];

    const tempValidFacilityIds: number[] = [];
    //Get valid facility id's
    for (let i = 0; i < ticket.valid_facilities.length; i++) {
      tempValidFacilityIds.push(ticket.valid_facilities[i].id);
    }

    for (let i = 0; i < facilityRes.data.length; i++) {
      const tempFacilityId = {
        id: facilityRes.data[i].id,
        value: facilityRes.data[i].id,
        label: facilityRes.data[i].long_name,
        isChecked: false,
      };

      tempFacilityIdList.push(tempFacilityId);
    }

    const currentExpiryDate = new Date(moment(ticket.expiry_date).format("LL"));

    setTicketState(prevState => ({
      ...prevState,
      id: ticket.id,
      title: ticket.title,
      subtitle: ticket.subtitle ?? "",
      description: ticket.description,
      expiry_date: ticket.expiry_date ?? "",
      expiry_days: ticket.expiry_days,
      expiry_type: ticket.expiry_type,
      logo_image: ticket.logo_image, // in GET
      selectedDate: currentExpiryDate,
      facility_ids_list: tempFacilityIdList,
      product_redemption: ticket.variant_redemption,
      ticket_stubs: ticket.stubs,
      archived: ticket.archived,
      terms: ticket.terms,
      client_id: ticket.client_id,
      valid_facilities: tempValidFacilityIds,
      share_limit: ticket?.share_limit?.toString(),
      transfers_disabled: ticket?.transfers_disabled,
      multi_use: !!ticket?.multi_use,
      valid_start_date: ticket.valid_start_date ? new Date(ticket.valid_start_date) : null,
      valid_end_date: ticket.valid_end_date ? new Date(ticket.valid_end_date) : null,
      variants: loadTicketRes.data[0].variants,
    }));

    setTicketLoaded(true);

    // terms: Array<string>; //
    // archived: boolean; //
    // client_id: number;
    // stubs: Array<TicketStubType>; //
  }

  async function saveTicket() {
    const formattedParams = {
      ...ticketState,
      valid_start_date: ticketState.valid_start_date ? ticketState.valid_start_date.toISOString().slice(0, 10) : null,
      valid_end_date: ticketState.valid_end_date ? ticketState.valid_end_date.toISOString().slice(0, 10) : null,
    };

    const ticketRes = await PutTicket(formattedParams, true);
    if (ticketRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.ticket_edit.034")));
      return;
    }
    dispatch(showSuccess(t("secure.facility.product.ticket_edit.035")));
    history.push("/admin/tickets");
  }

  function handleDropDownChange(value: any, property: string) {
    setTicketState(prev => ({ ...prev, [property]: value }));
  }

  const handleDateSelectorChange = (selectedDate: Date) => {
    const tempDate = new Date(selectedDate).toISOString().slice(0, 10);

    setTicketState(prevState => ({ ...prevState, expiry_date: tempDate, selectedDate: selectedDate }));
  };

  async function addRedemptionProduct() {
    let variants: Array<IVariant> = [];
    selectedProducts?.forEach(product => {
      variants = [...variants, ...product.variants.map(variant => variant)];
    });
    const formattedVariants = variants?.map(variant => ({ variant_id: variant?.id, quantity: variant?.quantity }));
    const params = {
      ticket_id: ticketId,
      variants: formattedVariants,
    };

    const redemptionRes = await AddRedemptionProducts(params, true);
    if (redemptionRes.status !== StatusCode.OK) {
      return;
    }

    const ticketParams = {
      id: ticketId,
      extended: true,
    };

    const loadTicketRes = await GetTickets(ticketParams, true);

    if (loadTicketRes.status !== StatusCode.OK) {
      return;
    }

    dispatch(showSuccess(t("secure.facility.product.ticket_edit.036")));

    ReactDOM.unstable_batchedUpdates(() => {
      setTicketState(prevState => ({
        ...prevState,
        product_redemption: loadTicketRes.data[0].variant_redemption,
      }));

      setTicketStateBeforeChanges(prevState => ({
        ...prevState,
        product_redemption: loadTicketRes.data[0].variant_redemption,
      }));
      setRedemptionProductModalOpen(false);
      setSelectedProducts([]);
    });
  }

  async function removeRedemptionProduct(newProduct: any) {
    const params = {
      ticket_id: ticketId,
      variant_id: newProduct.id,
      quantity: 1,
    };

    const redemptionRes = await RemoveRedemptionProduct(params, true);

    if (redemptionRes.status !== StatusCode.OK) {
      return;
    }

    const ticketParams = {
      id: ticketId,
      extended: true,
    };

    const loadTicketRes = await GetTickets(ticketParams, true);

    if (loadTicketRes.status !== StatusCode.OK) {
      return;
    }

    setTicketState(prevState => ({
      ...prevState,
      product_redemption: loadTicketRes.data[0].variant_redemption,
    }));

    setTicketStateBeforeChanges(prevState => ({
      ...prevState,
      product_redemption: loadTicketRes.data[0].variant_redemption,
    }));
  }

  async function deleteTicketImage() {
    const deleteRes = await DeleteTicketImage({ id: ticketId }, true);
    if (deleteRes.status !== StatusCode.OK) {
      console.log("Error deleting", deleteRes);
      return;
    }

    const ticket = deleteRes.data;

    setTicketState(prevState => ({
      ...prevState,
      id: ticket.id,
      title: ticket.title,
      description: ticket.description,
      expiry_date: ticket.expiry_date,
      expiry_days: ticket.expiry_days,
      expiry_type: ticket.expiry_type,
      logo_image: ticket.logo_image,
    }));

    setTicketStateBeforeChanges(prevState => ({
      ...prevState,
      id: ticket.id,
      title: ticket.title,
      description: ticket.description,
      expiry_date: ticket.expiry_date,
      expiry_days: ticket.expiry_days,
      expiry_type: ticket.expiry_type,
      logo_image: ticket.logo_image,
    }));
  }

  function imageUploadOnChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files;
    if (file && file.length > 0) {
      //Upload image after the file is selected
      void uploadTicketImage(file[0]);
    }
  }

  async function uploadTicketImage(imageFile: File) {
    const formData = new FormData();

    formData.append("logo_image", imageFile);
    formData.append("id", ticketId as string);

    //Reset file selector input value
    imageUploadInputRef.current.value = "";

    const imageRes = await PostTicketImage(formData, true);
    if (imageRes.status !== StatusCode.OK) {
      return;
    }

    const ticket = imageRes.data;

    setTicketState(prevState => ({
      ...prevState,
      id: ticket.id,
      title: ticket.title,
      description: ticket.description,
      expiry_date: ticket.expiry_date,
      expiry_days: ticket.expiry_days,
      expiry_type: ticket.expiry_type,
      logo_image: ticket.logo_image,
    }));

    setTicketStateBeforeChanges(prevState => ({
      ...prevState,
      id: ticket.id,
      title: ticket.title,
      description: ticket.description,
      expiry_date: ticket.expiry_date,
      expiry_days: ticket.expiry_days,
      expiry_type: ticket.expiry_type,
      logo_image: ticket.logo_image,
    }));
  }

  function unsavedChangesExist() {
    if (ticketStateBeforeChanges === undefined) {
      if (ticketLoaded) {
        setTicketStateBeforeChanges(ticketState);
      }
      return false;
    }

    return !isEqualWith(ticketStateBeforeChanges, ticketState, (originalValue, newValue) => {
      if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
        return true;
      }
    });
  }

  function cancelUnsavedChanges() {
    setTicketState(ticketStateBeforeChanges);
  }

  const disableAddRedemptionProducts = selectedProducts?.every(product =>
    product?.variants?.some(variant => variant?.quantity),
  );

  async function handleArchiveTicket() {
    const archiveRes = await ArchiveTicket({ id: ticketId }, true);
    if (archiveRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.ticket_edit.037")));
      return;
    }

    setFilterState(prevState => ({ ...prevState, showArchiveWarning: false }));
    history.push("/admin/tickets");
  }

  function handleCheckboxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = e.target;

    setTicketState(prev => ({ ...prev, [id]: checked }));
  }

  console.log("ticketState", ticketState);

  return (
    <Page
      title={ticketState.title}
      narrow
      breadcrumbs={[{ label: t("secure.facility.product.ticket_edit.038"), url: "/admin/tickets" }]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: saveTicket,
        onCancel: cancelUnsavedChanges,
      }}
      primaryAction={{
        content: t("secure.facility.product.ticket_edit.039"),
        action: () => setFilterState(prevState => ({ ...prevState, showArchiveWarning: true })),
        disabled: !authStore?.user?.permissions?.tickets_archive || ticketState.archived,
      }}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                value={ticketState.title}
                label={t("secure.facility.product.ticket_edit.002")}
                id="title"
                onChange={handleInputChange}
                placeholder={t("secure.facility.product.ticket_edit.003")}
              />
              <Input
                value={ticketState.subtitle}
                label={t("secure.facility.product.ticket_edit.030")}
                id="subtitle"
                onChange={handleInputChange}
                placeholder={t("secure.facility.product.ticket_edit.031")}
              />
            </FormLayout.Group>

            <FormLayout.Group>
              <Checkbox
                id={"transfers_disabled"}
                size="small"
                label={t("Disable Ticket Transfers")}
                supportText={t("Prevent these tickets from being transferred between players")}
                checked={ticketState?.transfers_disabled}
                onChange={e => handleCheckboxChange(e)}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
        <Card.Section>
          <TextEditor
            label={t("secure.facility.product.ticket_edit.040")}
            markdownText={ticketState.description ?? ""}
            markdownTextOnChange={handleDescriptionChange}
          />
        </Card.Section>
      </Card>

      <Card title={t("secure.facility.product.ticket_edit.010")}>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <div className="customer-image_grid">
                {ticketState.logo_image !== null || "" || undefined ? (
                  <div>
                    <div className="product-image_container">
                      <div className="product-image">
                        <div className="remove-button_container">
                          <div className="remove-button" onClick={() => deleteTicketImage()}>
                            <FontAwesomeIcon icon={faTimes} />
                          </div>
                        </div>
                        <img src={ticketState.logo_image} alt={t("profile image")} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="product-image-upload_container">
                      <div className="product-image-upload_content">
                        <input
                          ref={imageUploadInputRef}
                          className="product-image-upload_input"
                          type="file"
                          accept="image/*"
                          onChange={imageUploadOnChange}
                        />
                        <p>
                          <FontAwesomeIcon icon={faUpload} />
                        </p>
                      </div>
                    </div>
                    <div className="product-image-upload_content"></div>
                  </>
                )}
              </div>
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>

      <Card title={t("secure.facility.product.ticket_edit.011")}>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Select
                label={t("secure.facility.product.ticket_edit.012")}
                onChange={(value: any) => handleDropDownChange(value, "expiry_type")}
                defaultValue={ticketState.expiry_type}
              >
                {options?.map((option: any, index: number) => {
                  return (
                    <Option key={index} value={option.value} name={option.label}>
                      {option.label}
                    </Option>
                  );
                })}
              </Select>

              <div>
                {ticketState.expiry_type === "date" ? (
                  // <DateSelector onChange={handleDateSelectorChange} hideInput selectedDate={ticketState.selectedDate} />

                  <DatePickerInput
                    label={t("secure.facility.product.ticket_edit.041")}
                    months={1}
                    position="left"
                    startingDate={ticketState.selectedDate}
                    setStartingDate={handleDateSelectorChange}
                  />
                ) : null}

                {ticketState.expiry_type === "days" ? (
                  <Input
                    label={t("secure.facility.product.ticket_edit.013")}
                    id="expiry_days"
                    value={ticketState.expiry_days}
                    onChange={handleInputChange}
                    placeholder={t("secure.facility.product.ticket_edit.014")}
                  />
                ) : null}
              </div>
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <div className="w-1/2">
                <DatePickerInput
                  label={t("Valid Date Range")}
                  months={2}
                  position="left"
                  startingDate={ticketState.valid_start_date ?? new Date()}
                  setStartingDate={(date: Date) => setTicketState(prev => ({ ...prev, valid_start_date: date }))}
                  endingDate={ticketState.valid_end_date ?? new Date()}
                  setEndingDate={(date: Date) => setTicketState(prev => ({ ...prev, valid_end_date: date }))}
                />
              </div>
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>

        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <div className="w-1/2">
                <Input
                  value={ticketState.share_limit}
                  label={t("Share Limit")}
                  id="share_limit"
                  onChange={handleInputChange}
                  placeholder={t("0")}
                  type="number"
                  tooltipText={t("The maximum number of users the ticket can be shared with")}
                />
              </div>
            </FormLayout.Group>
            <FormLayout.Group>
              <Checkbox
                supportText={t("Allows ticket to generate a reusable barcode")}
                label={t("Multi Use")}
                checked={ticketState?.multi_use}
                onChange={e => setTicketState(prev => ({ ...prev, multi_use: e.target.checked }))}
                size="medium"
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>

        <Card.Section
          title={t("secure.facility.product.ticket_edit.018")}
          subtitle={t("secure.facility.product.ticket_edit.019")}
        >
          <FormLayout>
            {ticketState.facility_ids_list.map((facility: any, index: number) => {
              return (
                <FormLayout.Group key={index}>
                  <Checkbox
                    id={facility.id}
                    size="small"
                    checked={ticketState?.valid_facilities?.some(facilityId => facilityId === facility?.id)}
                    value={facility.value}
                    onChange={() => changeFacilityList(facility as IFacility)}
                    label={facility.label}
                  />
                </FormLayout.Group>
              );
            })}
          </FormLayout>
        </Card.Section>

        <Card.Section title={t("secure.facility.product.ticket_edit.020")}>
          <FormLayout>
            <FormLayout.Group>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "16px", paddingBottom: "8px" }}>
                  <h1>{t("secure.facility.product.ticket_edit.028")}</h1>
                  <Button size="small" onClick={() => setRedemptionProductModalOpen(true)}>
                    {t("secure.facility.product.ticket_edit.042")}
                  </Button>
                </div>

                {ticketState.product_redemption ? (
                  <div className="redemption-wrapper">
                    {ticketState.product_redemption.map((product: any, index: number) => {
                      let productTitle: string;

                      if (product.title === product.product.title) {
                        productTitle = String(product.title);
                      } else {
                        productTitle = String(product.product.title) + " - " + String(product.title);
                      }

                      return (
                        <div key={index} className="redemption-container">
                          <div className="redemption-product-title">{productTitle}</div>
                          <div className="font-normal">x{product.redemption_details.quantity}</div>
                          <div className="redemption-product-button">
                            <ButtonNew onClick={() => removeRedemptionProduct(product)}>
                              {t("secure.facility.product.ticket_edit.029")}
                            </ButtonNew>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <Callout type="info" title={t("No Redemption Products")} />
                  </>
                )}
              </div>
            </FormLayout.Group>

            <FormLayout.Group>
              <div className="redemption-wrapper">
                <div style={{ display: "flex", alignItems: "center", gap: "16px", paddingBottom: "8px" }}>
                  <h1>{t("Connected to")}</h1>
                </div>
                {ticketState?.variants?.length > 0 ? (
                  ticketState?.variants?.map((variant, variantIndex) => {
                    return (
                      <div key={variantIndex} className="redemption-container">
                        <div className="redemption-product-title">{variant.title}</div>
                        <div className="redemption-product-button">
                          <ButtonNew onClick={() => history.push("/admin/product/" + String(variant.product_id))}>
                            {t("View Product")}
                          </ButtonNew>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <>
                    <Callout type="info" title={t("Ticket is not attached to a product")} />
                  </>
                )}
              </div>
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>

      <Portal isMounted={filterState?.showArchiveWarning}>
        <Popup
          open={filterState.showArchiveWarning}
          title={t("secure.facility.product.ticket_edit.046")}
          description={t("secure.facility.product.ticket_edit.047")}
          type="warning"
          onCancel={() => setFilterState(prevState => ({ ...prevState, showArchiveWarning: false }))}
          onOk={() => handleArchiveTicket()}
          okText={t("secure.facility.product.ticket_edit.048")}
        />
      </Portal>

      {/* Redemption Modal */}
      <Portal isMounted={redemptionProductModalOpen}>
        <div className="pricing-sheets-products-modal">
          <Sheet
            closable
            onCancel={() =>
              ReactDOM.unstable_batchedUpdates(() => {
                setRedemptionProductModalOpen(false);
                setSelectedProducts([]);
              })
            }
            size="medium"
            open={redemptionProductModalOpen}
            cancelText={t("secure.facility.product.ticket_edit.043")}
            okText={t("secure.facility.product.ticket_edit.044")}
            title={t("secure.facility.product.ticket_edit.045")}
            okDisabled={selectedProducts?.length === 0 || !disableAddRedemptionProducts}
            onOk={addRedemptionProduct}
          >
            <ProductSelect
              selectedProducts={selectedProducts}
              search
              onChange={products => setSelectedProducts(products)}
              getProductsParams={{ extended_variants: true }}
              variantInput={{ key: "quantity", placeholder: t("secure.facility.product.ticket_edit.049") }}
              limit={50}
            />
          </Sheet>
        </div>
      </Portal>
    </Page>
  );
};

export default Single;
